// Documentazione API — sezioni "PlatformDocs". JSX estratto verbatim da DeveloperSettings.tsx.
import type { ReactElement } from 'react';
import { AlertCircle } from 'lucide-react';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@easyflow/ui-frontend/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@easyflow/ui-frontend/components/ui/table';
import { DocSection, Endpoint, CodeBlock } from './primitives';

export function renderPlatformDocSection(section: string): ReactElement | null {
  switch (section) {
      case 'webhooks':
        return (
          <DocSection id="webhooks">
            <h3 className="text-lg font-semibold">Webhooks API</h3>
            <p className="text-sm text-muted-foreground">
              Base: <code className="bg-muted px-1 rounded">/api/v2/voip/webhooks</code>.
              Lettura e listing eventi: scope <code className="bg-muted px-1 rounded">voip:read</code>.
              Create/update/delete/test: scope <code className="bg-muted px-1 rounded">voip:write</code>.
              In creazione/aggiornamento, gli <code>events</code> richiesti sono validati contro lo scope mapping della API Key (<code>SCOPE_VIOLATION</code> 400 se l'evento non è ammesso).
            </p>
            <Endpoint method="GET" path="/api/v2/voip/webhooks" desc="Lista delle configurazioni webhook del tenant. Il segreto NON è esposto; è restituito in chiaro solo nella response di POST." scope="voip:read" />
            <Endpoint method="POST" path="/api/v2/voip/webhooks" desc="Crea webhook. Obbligatori: name, url, events[]. secret opzionale (auto-generato 32 byte hex se omesso). retry_count default 3, timeout_ms default 5000. Richiede api_key_id nel context." scope="voip:write" />
            <Endpoint method="PUT" path="/api/v2/voip/webhooks/:id" desc="Aggiorna webhook (campi parziali). events[] rivalidato contro gli scope. Il secret torna sempre ***REDACTED*** in response." scope="voip:write" />
            <Endpoint method="DELETE" path="/api/v2/voip/webhooks/:id" desc="Elimina la configurazione webhook." scope="voip:write" />
            <Endpoint method="POST" path="/api/v2/voip/webhooks/:id/test" desc="Invia un evento ping di test all'endpoint configurato e ritorna esito + eventuale status code/HTTP body." scope="voip:write" />
            <Endpoint method="GET" path="/api/v2/voip/webhooks/:id/deliveries" desc="Storico delivery del webhook con paginazione (limit, offset). Include status_code, response_time_ms, attempt e payload." scope="voip:read" />
            <Endpoint method="GET" path="/api/v2/voip/webhooks/events" desc="Catalogo eventi disponibili: { categories, all_events[], total }. Utile per popolare il selettore in UI." scope="voip:read" />
            <h4 className="text-sm font-semibold mt-3">Available Event Types (50+ events in 11 categories)</h4>
            <div className="border rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted"><tr><th className="text-left p-2 font-semibold">Category</th><th className="text-left p-2 font-semibold">Events</th></tr></thead>
                <tbody>
                  <tr className="border-t"><td className="p-2 font-semibold">Call</td><td className="p-2 text-xs font-mono">call.start, call.ringing, call.answered, call.hold, call.unhold, call.transfer, call.ended, call.recording_ready, call.dtmf</td></tr>
                  <tr className="border-t"><td className="p-2 font-semibold">Trunk</td><td className="p-2 text-xs font-mono">trunk.created, trunk.updated, trunk.deleted, trunk.status, trunk.registered, trunk.unregistered</td></tr>
                  <tr className="border-t"><td className="p-2 font-semibold">Extension</td><td className="p-2 text-xs font-mono">extension.created, extension.updated, extension.deleted, extension.registered, extension.unregistered, extension.status</td></tr>
                  <tr className="border-t"><td className="p-2 font-semibold">Queue</td><td className="p-2 text-xs font-mono">queue.created, queue.updated, queue.deleted, queue.agent.login/logout/pause/unpause, queue.call.enter/answered/abandoned/timeout</td></tr>
                  <tr className="border-t"><td className="p-2 font-semibold">Ring Group</td><td className="p-2 text-xs font-mono">ring_group.created, ring_group.updated, ring_group.deleted</td></tr>
                  <tr className="border-t"><td className="p-2 font-semibold">Routing</td><td className="p-2 text-xs font-mono">inbound_route.created/updated/deleted, outbound_route.created/updated/deleted</td></tr>
                  <tr className="border-t"><td className="p-2 font-semibold">Voicemail</td><td className="p-2 text-xs font-mono">voicemail.new, voicemail.read, voicemail.deleted</td></tr>
                  <tr className="border-t"><td className="p-2 font-semibold">Recording</td><td className="p-2 text-xs font-mono">recording.ready, recording.deleted</td></tr>
                  <tr className="border-t"><td className="p-2 font-semibold">DID</td><td className="p-2 text-xs font-mono">did.assigned, did.unassigned</td></tr>
                  <tr className="border-t"><td className="p-2 font-semibold">System</td><td className="p-2 text-xs font-mono">system.health, api.error, sync.completed, sync.failed</td></tr>
                  <tr className="border-t"><td className="p-2 font-semibold">AI</td><td className="p-2 text-xs font-mono">call.transcript (chunk + final aggregato), agent.tool_call</td></tr>
                </tbody>
              </table>
            </div>
            <h4 className="text-sm font-semibold mt-3">Webhook Features</h4>
            <p className="text-sm text-muted-foreground">Per-webhook HMAC-SHA256 secret, circuit breaker (10 failures = 5min cooldown), exponential backoff retries (2s/4s/8s), custom headers support.</p>
            <h4 className="text-sm font-semibold mt-3">Webhook Payload</h4>
            <CodeBlock>{`{
  "type": "extension.created",
  "tenant_id": "uuid",
  "timestamp": "2025-11-30T10:00:00Z",
  "data": {
    "id": "uuid",
    "extension": "1001",
    "display_name": "John Doe",
    "type": "user"
  }
}`}</CodeBlock>
            <h4 className="text-sm font-semibold mt-3">Webhook Headers</h4>
            <div className="border rounded-md overflow-hidden mt-1">
              <table className="w-full text-sm">
                <thead className="bg-muted"><tr><th className="text-left p-2 font-semibold">Header</th><th className="text-left p-2 font-semibold">Description</th></tr></thead>
                <tbody>
                  <tr className="border-t"><td className="p-2 font-mono text-xs">X-Webhook-Signature</td><td className="p-2">HMAC-SHA256 signature: sha256=...</td></tr>
                  <tr className="border-t"><td className="p-2 font-mono text-xs">X-Webhook-Event</td><td className="p-2">Event type (e.g. extension.created)</td></tr>
                  <tr className="border-t"><td className="p-2 font-mono text-xs">X-Webhook-Timestamp</td><td className="p-2">Unix epoch (seconds)</td></tr>
                  <tr className="border-t"><td className="p-2 font-mono text-xs">X-Request-ID</td><td className="p-2">Unique delivery ID</td></tr>
                </tbody>
              </table>
            </div>
            <h4 className="text-sm font-semibold mt-3">Signature Verification</h4>
            <CodeBlock title="Node.js Verification">{`const crypto = require('crypto');
const signature = req.headers['x-webhook-signature'].replace('sha256=', '');
const expected = crypto
  .createHmac('sha256', WEBHOOK_SECRET)
  .update(JSON.stringify(req.body))
  .digest('hex');
const valid = crypto.timingSafeEqual(
  Buffer.from(signature), Buffer.from(expected)
);`}</CodeBlock>

            <h4 className="text-sm font-semibold mt-6">Hardening v2 (Task #1170) — Streaming events & schema changes</h4>
            <p className="text-sm text-muted-foreground">Eventi aggiuntivi e modifiche di schema introdotti per le integrazioni di terze parti.</p>

            <h5 className="text-sm font-semibold mt-3"><code className="bg-muted px-1 rounded">call.transcript</code> — chunked + final</h5>
            <p className="text-sm text-muted-foreground">Emesso per ogni frammento STT dai provider voice (gemini-live, openai-realtime, openai-sip). A chiusura chiamata <strong>tutti e tre i provider</strong> emettono anche un payload finale aggregato con <code className="bg-muted px-1 rounded">is_final: true</code> e <code className="bg-muted px-1 rounded">messages[]</code>.</p>
            <CodeBlock title="Chunk live">{`{
  "type": "call.transcript",
  "data": {
    "call_uuid": "fs-call-uuid",
    "agent_id": "uuid",
    "provider": "gemini-live",
    "speaker": "user",
    "text": "Vorrei prenotare una visita",
    "start_offset_ms": 12450,
    "end_offset_ms": 12450,
    "confidence": null,
    "language": null,
    "is_final": false
  }
}`}</CodeBlock>
            <CodeBlock title="Final transcript a chiusura chiamata">{`{
  "type": "call.transcript",
  "data": {
    "call_uuid": "fs-call-uuid",
    "is_final": true,
    "messages": [
      { "role": "user",      "text": "...", "timestamp": "..." },
      { "role": "assistant", "text": "...", "timestamp": "..." }
    ]
  }
}`}</CodeBlock>

            <h5 className="text-sm font-semibold mt-3"><code className="bg-muted px-1 rounded">agent.tool_call</code> — schema</h5>
            <p className="text-sm text-muted-foreground">⚠️ <strong>Breaking</strong> rispetto a payload precedenti: il campo si chiama <code className="bg-muted px-1 rounded">execution_time_ms</code> (NON <code>duration_ms</code>) e <code className="bg-muted px-1 rounded">tool_provider</code> usa l'enum <code>mcp</code> / <code>native</code> / <code>external</code> (rimossi <code>builtin</code> e <code>mcp_gateway</code>).</p>
            <CodeBlock>{`{
  "type": "agent.tool_call",
  "data": {
    "call_uuid": "fs-call-uuid",
    "agent_id": "uuid",
    "tool_provider": "mcp",        // mcp | native | external
    "tool_name": "lookup_customer",
    "input_params": { "phone": "+39021234567" },
    "output": { "customer_id": "uuid" },
    "success": true,
    "invoked_at": "2026-05-02T10:00:18.150Z",
    "execution_time_ms": 70,
    "error_message": null,
    "channel": "voice",
    "provider": "gemini-live",
    "model": "gemini-2.0-flash-exp"
  }
}`}</CodeBlock>
            <ul className="text-sm text-muted-foreground list-disc pl-5 mt-2">
              <li><code className="bg-muted px-1 rounded">native</code> — strumento built-in del dispatcher EDGVoIP.</li>
              <li><code className="bg-muted px-1 rounded">mcp</code> — strumento esposto da un MCP server collegato (in-process).</li>
              <li><code className="bg-muted px-1 rounded">external</code> — invocazione arrivata dal gateway HTTP MCP da un client esterno (no <code>call_uuid</code>).</li>
            </ul>

            <h5 className="text-sm font-semibold mt-3"><code className="bg-muted px-1 rounded">recording.ready</code> — presigned <code>download_url</code></h5>
            <p className="text-sm text-muted-foreground">
              Su storage cloud (R2/S3) <code className="bg-muted px-1 rounded">download_url</code> è un URL HTTPS pre-firmato dal provider (24h). Su storage locale è assoluto (<code>PUBLIC_BASE_URL</code> o <code>https://$REPLIT_DEV_DOMAIN</code>) firmato HMAC con <code>WEBHOOK_SIGNING_SECRET</code>. <strong>Il fallback <code>dev-recording-secret</code> è stato rimosso</strong>: senza un segreto configurato il campo viene omesso e l'errore loggato. L'alias <code className="bg-muted px-1 rounded">call.recording_ready</code> ha lo stesso payload ed è inviato in parallelo per backward compatibility.
            </p>

            <h5 className="text-sm font-semibold mt-3"><code className="bg-muted px-1 rounded">call.start</code> — SIP URIs e header X-*</h5>
            <p className="text-sm text-muted-foreground">Il payload include <code className="bg-muted px-1 rounded">sip_from_uri</code>, <code className="bg-muted px-1 rounded">sip_to_uri</code> e un dict <code className="bg-muted px-1 rounded">sip_headers</code> contenente <strong>solo</strong> gli header SIP che iniziano con <code>X-</code>. Header standard (Authorization, Contact, Via, Route, Record-Route, To, From) sono filtrati. Il valore <code className="bg-muted px-1 rounded">speaker</code> di <code>call.transcript</code> è normalizzato a <code>user</code> | <code>assistant</code> per tutti i provider.</p>

            <h5 className="text-sm font-semibold mt-3">Scope mapping</h5>
            <div className="border rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted"><tr><th className="text-left p-2 font-semibold">Evento</th><th className="text-left p-2 font-semibold">Scope ammessi</th></tr></thead>
                <tbody>
                  <tr className="border-t"><td className="p-2 font-mono text-xs">call.start, call.ringing, call.answered, call.hold, call.unhold, call.transfer, call.dtmf</td><td className="p-2 font-mono text-xs">voip:read</td></tr>
                  <tr className="border-t"><td className="p-2 font-mono text-xs">call.ended</td><td className="p-2 font-mono text-xs">voip:read · cdr:export</td></tr>
                  <tr className="border-t"><td className="p-2 font-mono text-xs">recording.ready, call.recording_ready</td><td className="p-2 font-mono text-xs">voip:read · recordings:access</td></tr>
                  <tr className="border-t"><td className="p-2 font-mono text-xs">call.transcript (chunk + final)</td><td className="p-2 font-mono text-xs">voip:read</td></tr>
                  <tr className="border-t"><td className="p-2 font-mono text-xs">agent.tool_call</td><td className="p-2 font-mono text-xs">voip:read · ai:config</td></tr>
                  <tr className="border-t"><td className="p-2 font-mono text-xs">extension.created, extension.updated, extension.deleted</td><td className="p-2 font-mono text-xs">voip:write · extensions:write</td></tr>
                  <tr className="border-t"><td className="p-2 font-mono text-xs">extension.registered, extension.unregistered, extension.status</td><td className="p-2 font-mono text-xs">voip:read · extensions:read</td></tr>
                  <tr className="border-t"><td className="p-2 font-mono text-xs">trunk.created/updated/deleted, queue.* (CRUD), ring_group.* (CRUD), inbound_route.* (CRUD), outbound_route.* (CRUD)</td><td className="p-2 font-mono text-xs">voip:write</td></tr>
                </tbody>
              </table>
            </div>

            <h5 className="text-sm font-semibold mt-3">Extensions — rotazione password</h5>
            <p className="text-sm text-muted-foreground">
              <code className="bg-muted px-1 rounded">POST /api/v2/voip/extensions/:external_id/reset-password</code> genera una password URL-safe a 24 caratteri, propaga le credenziali al directory FreeSWITCH (best-effort, non blocca la response) ed emette <code className="bg-muted px-1 rounded">extension.updated</code>. La password in chiaro è restituita <strong>solo</strong> nella response immediata della rotazione e non viene mai persistita né rimessa via <code>GET</code>.
            </p>
          </DocSection>
        );

      case 'health':
        return (
          <DocSection id="health">
            <h3 className="text-lg font-semibold">Health & Status API</h3>
            <p className="text-sm text-muted-foreground">Base: <code className="bg-muted px-1 rounded">/api/v2/voip</code></p>
            <Endpoint method="GET" path="/health" desc="API health check" />
            <Endpoint method="GET" path="/status/trunks" desc="Get all trunk registration statuses" />
            <Endpoint method="GET" path="/status/extensions" desc="Get all extension statuses" />
            <CodeBlock title="Health Response">{`{
  "success": true,
  "data": {
    "status": "healthy",
    "version": "2.0",
    "uptime": 86400,
    "timestamp": "2025-11-30T12:00:00Z"
  }
}`}</CodeBlock>
          </DocSection>
        );

      case 'errors':
        return (
          <DocSection id="errors">
            <h3 className="text-lg font-semibold">Error Codes & Rate Limiting</h3>
            <h4 className="text-sm font-semibold mt-3">HTTP Status Codes</h4>
            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Meaning</TableHead></TableRow></TableHeader>
                <TableBody>
                  <TableRow><TableCell className="font-mono">200</TableCell><TableCell>Success</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono">201</TableCell><TableCell>Created</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono">400</TableCell><TableCell>Bad Request - Invalid parameters</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono">401</TableCell><TableCell>Unauthorized - Invalid or missing API key</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono">403</TableCell><TableCell>Forbidden - Insufficient scope</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono">404</TableCell><TableCell>Not Found</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono">409</TableCell><TableCell>Conflict - Resource already exists</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono">429</TableCell><TableCell>Rate Limited</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono">500</TableCell><TableCell>Internal Server Error</TableCell></TableRow>
                </TableBody>
              </Table>
            </div>
            <h4 className="text-sm font-semibold mt-4">Error Response Format</h4>
            <CodeBlock>{`{
  "success": false,
  "error": "Human readable error message",
  "error_code": "MACHINE_READABLE_CODE",
  "details": {}
}`}</CodeBlock>
            <h4 className="text-sm font-semibold mt-4">Rate Limiting</h4>
            <p className="text-sm text-muted-foreground">API requests are limited to <strong>100 requests per minute</strong> per API key. Rate limit headers are included in all responses:</p>
            <div className="border rounded-md overflow-hidden mt-2">
              <Table>
                <TableHeader><TableRow><TableHead>Header</TableHead><TableHead>Description</TableHead></TableRow></TableHeader>
                <TableBody>
                  <TableRow><TableCell className="font-mono text-xs">X-RateLimit-Limit</TableCell><TableCell>Requests allowed per window</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">X-RateLimit-Remaining</TableCell><TableCell>Requests remaining</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">X-RateLimit-Reset</TableCell><TableCell>Window reset timestamp (Unix)</TableCell></TableRow>
                </TableBody>
              </Table>
            </div>
          </DocSection>
        );

      case 'mcp-tools-overview':
        return (
          <DocSection id="mcp-tools-overview">
            <h3 className="text-lg font-semibold">MCP Tools Overview</h3>
            <p className="text-sm text-muted-foreground">
              I <strong>MCP Tools</strong> (Model Context Protocol) sono strumenti che estendono le capacità degli AI agents, consentendo loro di interagire con sistemi esterni, database, API e servizi di terze parti in modo strutturato e sicuro.
            </p>

            <h4 className="text-sm font-semibold mt-4">Organizzazione dei Tools</h4>
            <p className="text-sm text-muted-foreground">I tools sono organizzati in due categorie principali:</p>
            <div className="border rounded-md overflow-hidden mt-2">
              <Table>
                <TableHeader><TableRow><TableHead>Tipo</TableHead><TableHead>Descrizione</TableHead></TableRow></TableHeader>
                <TableBody>
                  <TableRow><TableCell className="font-semibold">Internal Tools</TableCell><TableCell>Tools built-in di EDG VoIP, disponibili di default per tutti i tenant</TableCell></TableRow>
                  <TableRow><TableCell className="font-semibold">External Tools</TableCell><TableCell>Tools provenienti da MCP servers esterni connessi al sistema</TableCell></TableRow>
                </TableBody>
              </Table>
            </div>

            <h4 className="text-sm font-semibold mt-4">Categorie di Tools</h4>
            <div className="flex flex-wrap gap-2 mt-1">
              {['telephony', 'crm', 'hospitality', 'workflow', 'calendar', 'messaging', 'campaign', 'ai', 'knowledge'].map((cat) => (
                <Badge key={cat} variant="secondary" className="text-xs">{cat}</Badge>
              ))}
            </div>

            <h4 className="text-sm font-semibold mt-4">Tool Metadata</h4>
            <p className="text-sm text-muted-foreground">Ogni tool include i seguenti metadati:</p>
            <div className="border rounded-md overflow-hidden mt-2">
              <Table>
                <TableHeader><TableRow><TableHead>Campo</TableHead><TableHead>Tipo</TableHead><TableHead>Descrizione</TableHead></TableRow></TableHeader>
                <TableBody>
                  <TableRow><TableCell className="font-mono text-xs">tags</TableCell><TableCell>string[]</TableCell><TableCell>Categorie e etichette per la classificazione del tool</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">priority</TableCell><TableCell>number (1-5)</TableCell><TableCell>Priorità del tool (1 = massima, 5 = minima)</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">short_description</TableCell><TableCell>string</TableCell><TableCell>Descrizione breve per la UI e per il context dell'AI agent</TableCell></TableRow>
                </TableBody>
              </Table>
            </div>

            <h4 className="text-sm font-semibold mt-4">Abilitazione Tools per Agent</h4>
            <p className="text-sm text-muted-foreground">
              Ogni AI agent può avere un massimo di <code className="bg-muted px-1 rounded">128 tools</code> abilitati simultaneamente (limite imposto dalla OpenAI Realtime API). I tools possono essere abilitati o disabilitati individualmente o in batch tramite le API di Agent Bindings.
            </p>
            <Alert className="mt-2">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>MAX_TOOLS_PER_AGENT = 128</AlertTitle>
              <AlertDescription className="text-xs">Limite massimo di tools per singolo agent, definito dalla OpenAI Realtime API. Tutte le route di permission enforcement rispettano questo limite.</AlertDescription>
            </Alert>

            <h4 className="text-sm font-semibold mt-4">API Endpoints</h4>
            <p className="text-sm text-muted-foreground">Base: <code className="bg-muted px-1 rounded">/api/v2/voip/mcp</code></p>
            <Endpoint method="GET" path="/mcp/servers" desc="Lista tutti i MCP servers configurati per il tenant" />
            <Endpoint method="GET" path="/mcp/servers/:id/tools" desc="Lista i tools disponibili per un MCP server specifico" />
            <Endpoint method="POST" path="/mcp/servers/:id/sync" desc="Sincronizza i tools dal MCP server remoto" />
            <CodeBlock title="Response Example - GET /mcp/servers/:id/tools">{`{
  "success": true,
  "data": [{
    "id": "uuid",
    "name": "search_contacts",
    "description": "Search CRM contacts by name or phone",
    "category": "crm",
    "tags": ["crm", "search", "contacts"],
    "priority": 2,
    "short_description": "Cerca contatti nel CRM",
    "input_schema": { "type": "object", "properties": { "query": { "type": "string" } } },
    "enabled": true
  }]
}`}</CodeBlock>

            <h4 className="text-sm font-semibold mt-6">Disambiguazione tool Calendario</h4>
            <p className="text-sm text-muted-foreground">
              I tool calendario espongono i campi <code className="bg-muted px-1 rounded">subcategory</code> e{' '}
              <code className="bg-muted px-1 rounded">intended_use</code> per aiutare l&apos;AI agent a scegliere
              il tool corretto in base al <code className="bg-muted px-1 rounded">auth_context</code> corrente
              (<Badge variant="outline" className="text-xs mx-1">public</Badge> vs{' '}
              <Badge variant="outline" className="text-xs mx-1">internal</Badge>).
            </p>

            <div className="border rounded-md overflow-hidden mt-2">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tool</TableHead>
                    <TableHead>Subcategory</TableHead>
                    <TableHead>Context</TableHead>
                    <TableHead>Quando usarlo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-mono text-xs">check_availability</TableCell>
                    <TableCell><Badge variant="secondary" className="text-xs">calendar.read</Badge></TableCell>
                    <TableCell><Badge className="bg-blue-500/15 text-blue-700 dark:text-blue-400 text-xs">public + internal</Badge></TableCell>
                    <TableCell className="text-xs">Leggere slot disponibili prima di prenotare.</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-mono text-xs">find_qualified_resource</TableCell>
                    <TableCell><Badge variant="secondary" className="text-xs">calendar.read</Badge></TableCell>
                    <TableCell><Badge className="bg-blue-500/15 text-blue-700 dark:text-blue-400 text-xs">public + internal</Badge></TableCell>
                    <TableCell className="text-xs">Trovare risorse che erogano un servizio.</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-mono text-xs">list_pos_service_types</TableCell>
                    <TableCell><Badge variant="secondary" className="text-xs">calendar.read</Badge></TableCell>
                    <TableCell><Badge className="bg-blue-500/15 text-blue-700 dark:text-blue-400 text-xs">public + internal</Badge></TableCell>
                    <TableCell className="text-xs">Elencare tipi appuntamento disponibili.</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-mono text-xs">list_appointments</TableCell>
                    <TableCell><Badge variant="secondary" className="text-xs">calendar.read</Badge></TableCell>
                    <TableCell><Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-400 text-xs">internal</Badge></TableCell>
                    <TableCell className="text-xs">Operatore interno che consulta l&apos;agenda.</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-mono text-xs">create_appointment_with_customer</TableCell>
                    <TableCell><Badge variant="secondary" className="text-xs">calendar.public-form</Badge></TableCell>
                    <TableCell><Badge className="bg-green-500/15 text-green-700 dark:text-green-400 text-xs">public</Badge></TableCell>
                    <TableCell className="text-xs">Prenotazione da chatbot/form pubblico (rispetta auto_confirm).</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-mono text-xs">create_appointment</TableCell>
                    <TableCell><Badge variant="secondary" className="text-xs">calendar.internal</Badge></TableCell>
                    <TableCell><Badge className="bg-red-500/15 text-red-700 dark:text-red-400 text-xs">internal only</Badge></TableCell>
                    <TableCell className="text-xs">Operatore autenticato che crea direttamente.</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-mono text-xs">book_service</TableCell>
                    <TableCell><Badge variant="secondary" className="text-xs">calendar.internal</Badge></TableCell>
                    <TableCell><Badge className="bg-red-500/15 text-red-700 dark:text-red-400 text-xs">internal only</Badge></TableCell>
                    <TableCell className="text-xs">Prenotazione rapida con auto-assegnazione risorsa.</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-mono text-xs">update_appointment</TableCell>
                    <TableCell><Badge variant="secondary" className="text-xs">calendar.internal</Badge></TableCell>
                    <TableCell><Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-400 text-xs">internal</Badge></TableCell>
                    <TableCell className="text-xs">Modifica dell&apos;appuntamento.</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-mono text-xs">cancel_appointment</TableCell>
                    <TableCell><Badge variant="secondary" className="text-xs">calendar.internal</Badge></TableCell>
                    <TableCell><Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-400 text-xs">internal</Badge></TableCell>
                    <TableCell className="text-xs">Annullamento non distruttivo (stato &quot;cancelled&quot;).</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-mono text-xs">delete_appointment</TableCell>
                    <TableCell><Badge variant="secondary" className="text-xs">calendar.internal</Badge></TableCell>
                    <TableCell><Badge className="bg-red-500/15 text-red-700 dark:text-red-400 text-xs">internal only</Badge></TableCell>
                    <TableCell className="text-xs">Cancellazione definitiva — preferire cancel_appointment.</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-mono text-xs">schedule_callback</TableCell>
                    <TableCell><Badge variant="secondary" className="text-xs">calendar.internal</Badge></TableCell>
                    <TableCell><Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-400 text-xs">internal</Badge></TableCell>
                    <TableCell className="text-xs">Richiamata programmata (voicebot/operatore).</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            <h4 className="text-sm font-semibold mt-4">Decision tree</h4>
            <CodeBlock title="Selezione tool in base a auth_context">{`auth_context = "public"
  ├── check_availability / find_qualified_resource / list_pos_service_types  (read)
  ├── create_appointment_with_customer                                       (public-form)
  └── request_customer_otp / verify_customer_otp                             (verification)
      ↓ dopo verifica OTP
      └── cancel_appointment (solo dell'OTP verificato)

auth_context = "internal"
  ├── tutti i tool calendar.read
  ├── create_appointment / book_service     (creazione rapida)
  ├── update_appointment / cancel_appointment / schedule_callback
  └── delete_appointment                    (⚠ irreversibile)

⛔  In context public i tool { create_appointment, book_service, delete_appointment }
    sono rifiutati dal server con errore che indica il tool corretto.`}</CodeBlock>

            <h4 className="text-sm font-semibold mt-4">Esempi per scenario</h4>
            <CodeBlock title="Chatbot pubblico (auth_context='public')">{`// L'utente chiede: "Voglio prenotare un taglio per venerdì alle 15"
// ✅  Flusso corretto:
1. list_pos_service_types()            // mostra servizi
2. check_availability({ date: "2026-04-24" })
3. create_appointment_with_customer({
     form_slug: "prenota-salone",
     service_id: "...",
     start_time: "2026-04-24T15:00:00Z",
     customer: { name, email, phone, privacy_accepted: true },
     source: "chatbot_web"
   })

// ❌ Se l'AI prova create_appointment riceve:
// "Tool 'create_appointment' bloccato in context public (calendar.internal).
//  Usa 'create_appointment_with_customer (tool atomico ...)'
//  per prenotazioni da chatbot/form pubblici."`}</CodeBlock>

            <CodeBlock title="Voicebot inbound con OTP (auth_context='public' → 'internal')">{`1. request_customer_otp({ phone: "+39..." })
2. verify_customer_otp({ phone, code })   // promuove a internal per la sessione
3. create_appointment_with_customer({ ... })   // oppure create_appointment se abilitato`}</CodeBlock>

            <CodeBlock title="Operatore interno (auth_context='internal')">{`1. list_pos_service_types()
2. check_availability({ date, user_id })
3. create_appointment({ title, start_time, customer_name, ... })
// oppure
   book_service({ service_name, date, time, customer_name, customer_phone })`}</CodeBlock>

          </DocSection>
        );

      case 'mcp-agent-bindings':
        return (
          <DocSection id="mcp-agent-bindings">
            <h3 className="text-lg font-semibold">Agent Bindings</h3>
            <p className="text-sm text-muted-foreground">
              Gli <strong>Agent Bindings</strong> permettono di collegare specifici MCP tools agli AI agents, gestendo i permessi di accesso in modo granulare. Ogni binding definisce quali tools un agent può utilizzare durante le conversazioni.
            </p>

            <h4 className="text-sm font-semibold mt-4">Gestione Permessi</h4>
            <p className="text-sm text-muted-foreground">
              I permessi possono essere gestiti singolarmente o in batch. Il sistema verifica automaticamente che il numero totale di tools abilitati per agent non superi il limite di <strong>128 tools</strong>.
            </p>

            <h4 className="text-sm font-semibold mt-4">API Endpoints</h4>
            <p className="text-sm text-muted-foreground">Base: <code className="bg-muted px-1 rounded">/api/v2/voip/mcp</code></p>
            <Endpoint method="GET" path="/mcp/agent-permissions" desc="Lista tutti i permessi agent-tool per il tenant" />
            <Endpoint method="PUT" path="/mcp/agent-permissions" desc="Imposta il permesso per un singolo tool su un agent" />
            <Endpoint method="PUT" path="/mcp/agent-permissions/batch" desc="Imposta permessi multipli in un'unica operazione batch" />
            <Endpoint method="PUT" path="/mcp/agent-permissions/batch-by-filter" desc="Imposta permessi in batch usando filtri per domain o tags" />

            <h4 className="text-sm font-semibold mt-4">Batch Operations con Filter</h4>
            <p className="text-sm text-muted-foreground">
              Le operazioni batch-by-filter consentono di abilitare o disabilitare tools in base a criteri come <code className="bg-muted px-1 rounded">domain</code> (MCP server di origine) o <code className="bg-muted px-1 rounded">tags</code> (categorie del tool).
            </p>

            <CodeBlock title="PUT /mcp/agent-permissions - Single Permission">{`{
  "agent_id": "uuid-of-agent",
  "tool_id": "uuid-of-tool",
  "enabled": true
}`}</CodeBlock>

            <CodeBlock title="PUT /mcp/agent-permissions/batch">{`{
  "agent_id": "uuid-of-agent",
  "permissions": [
    { "tool_id": "uuid-tool-1", "enabled": true },
    { "tool_id": "uuid-tool-2", "enabled": true },
    { "tool_id": "uuid-tool-3", "enabled": false }
  ]
}`}</CodeBlock>

            <CodeBlock title="PUT /mcp/agent-permissions/batch-by-filter">{`// filter_type: "server" | "category" | "all" | "tags"
// Esempio 1 — per server:
{
  "agent_id": "uuid-of-agent",
  "filter_type": "server",
  "filter_value": "uuid-of-server",
  "enabled": true
}

// Esempio 2 — per tags (servizio gmail/drive/docs/sheets/calendar/
// contacts/meet e/o capacità read/write; il tool deve avere TUTTI i tag):
{
  "agent_id": "uuid-of-agent",
  "filter_type": "tags",
  "filter_value": ["gmail", "read"],
  "enabled": true
}`}</CodeBlock>

            <CodeBlock title="Response Example">{`{
  "success": true,
  "data": {
    "agent_id": "uuid-of-agent",
    "enabled_tools_count": 45,
    "max_tools": 128,
    "updated": 12
  }
}`}</CodeBlock>

            <Alert className="mt-2">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Limite 128 Tools</AlertTitle>
              <AlertDescription className="text-xs">Tutte le route di permessi verificano il limite di 128 tools per agent. Se un'operazione batch supererebbe il limite, viene restituito un errore 400 con il conteggio attuale.</AlertDescription>
            </Alert>
          </DocSection>
        );

      case 'mcp-workflows':
        return (
          <DocSection id="mcp-workflows">
            <h3 className="text-lg font-semibold">Workflows & Actions</h3>
            <p className="text-sm text-muted-foreground">
              I <strong>Workflows</strong> sono sequenze automatizzate di azioni che vengono eseguite in risposta a eventi specifici. Permettono di creare automazioni complesse senza scrivere codice, utilizzando il Workflow Builder visuale.
            </p>

            <h4 className="text-sm font-semibold mt-4">Workflow Builder</h4>
            <p className="text-sm text-muted-foreground">
              Il Workflow Builder è uno strumento visuale drag-and-drop per creare automazioni. Ogni workflow è composto da un trigger (evento scatenante) e una sequenza di azioni da eseguire.
            </p>

            <h4 className="text-sm font-semibold mt-4">Tipi di Trigger</h4>
            <div className="border rounded-md overflow-hidden mt-2">
              <Table>
                <TableHeader><TableRow><TableHead>Trigger</TableHead><TableHead>Descrizione</TableHead></TableRow></TableHeader>
                <TableBody>
                  <TableRow><TableCell className="font-mono text-xs">manual</TableCell><TableCell>Esecuzione manuale tramite UI o API call</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">scheduled</TableCell><TableCell>Esecuzione programmata con cron expression (es. ogni giorno alle 9:00)</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">event-based</TableCell><TableCell>Esecuzione automatica in risposta a eventi di sistema (call.ended, contact.created, etc.)</TableCell></TableRow>
                </TableBody>
              </Table>
            </div>

            <h4 className="text-sm font-semibold mt-4">Workflow Actions</h4>
            <p className="text-sm text-muted-foreground">
              Le azioni disponibili provengono dal registry delle <code className="bg-muted px-1 rounded">registry_functions</code> con il campo <code className="bg-muted px-1 rounded">exposed_as</code> che contiene <code className="bg-muted px-1 rounded">'workflow'</code>. Includono operazioni come invio SMS, aggiornamento CRM, notifiche, chiamate API esterne e molto altro.
            </p>

            <h4 className="text-sm font-semibold mt-4">Variabili e Template System</h4>
            <p className="text-sm text-muted-foreground">
              I workflow supportano variabili dinamiche accessibili tramite template syntax <code className="bg-muted px-1 rounded">{'{{variable_name}}'}</code>. Le variabili vengono popolate dai dati del trigger e dai risultati delle azioni precedenti nella sequenza.
            </p>

            <h4 className="text-sm font-semibold mt-4">API Endpoints</h4>
            <p className="text-sm text-muted-foreground">Base: <code className="bg-muted px-1 rounded">/api/v2/voip/workflows</code></p>
            <Endpoint method="GET" path="/workflows" desc="Lista tutti i workflows del tenant con filtri e paginazione" />
            <Endpoint method="POST" path="/workflows" desc="Crea un nuovo workflow con trigger e sequenza di azioni" />
            <Endpoint method="PUT" path="/workflows/:id" desc="Aggiorna un workflow esistente (configurazione, azioni, trigger)" />
            <Endpoint method="DELETE" path="/workflows/:id" desc="Elimina un workflow e tutte le sue esecuzioni programmate" />

            <CodeBlock title="POST /workflows - Creazione Workflow">{`{
  "name": "Notifica SMS dopo chiamata persa",
  "description": "Invia un SMS al chiamante quando una chiamata inbound non riceve risposta",
  "enabled": true,
  "trigger": {
    "type": "event-based",
    "event": "call.ended",
    "conditions": {
      "direction": "inbound",
      "status": "no_answer"
    }
  },
  "actions": [
    {
      "type": "send_sms",
      "config": {
        "to": "{{trigger.caller_number}}",
        "message": "Grazie per aver chiamato. Ti ricontatteremo al più presto."
      }
    },
    {
      "type": "create_crm_task",
      "config": {
        "title": "Richiamare {{trigger.caller_number}}",
        "priority": "high",
        "assigned_to": "{{trigger.destination_extension}}"
      }
    }
  ]
}`}</CodeBlock>

            <CodeBlock title="Response Example">{`{
  "success": true,
  "data": {
    "id": "uuid",
    "name": "Notifica SMS dopo chiamata persa",
    "enabled": true,
    "trigger": { "type": "event-based", "event": "call.ended" },
    "actions_count": 2,
    "last_execution": null,
    "created_at": "2026-01-15T10:00:00Z"
  }
}`}</CodeBlock>
          </DocSection>
        );

    default:
      return null;
  }
}
